import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Code, Users, GraduationCap, Building, Award, Search, 
  Filter, RefreshCw, TrendingUp, BarChart3, Eye, ChevronDown
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CodeArenaStudentsAdmin = () => {
  const [students, setStudents] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_year: {}, by_college: {} });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentSubmissions, setStudentSubmissions] = useState([]);

  useEffect(() => {
    fetchStudents();
  }, [filterYear]);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const params = new URLSearchParams();
      if (filterYear) params.append('year', filterYear);
      
      const response = await axios.get(`${API}/admin/codearena/students?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudents(response.data.students);
      setStats(response.data.stats);
    } catch (err) {
      toast.error('Failed to fetch students');
    } finally {
      setLoading(false);
    }
  };

  const fetchStudentDetail = async (studentId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/codearena/students/${studentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedStudent(response.data.student);
      setStudentSubmissions(response.data.recent_submissions);
    } catch (err) {
      toast.error('Failed to fetch student details');
    }
  };

  const filteredStudents = students.filter(s =>
    s.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.college?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Code className="text-indigo-600" size={28} />
            EvalentX Arena Students
          </h2>
          <p className="text-gray-600 mt-1">Manage individual student registrations and performance</p>
        </div>
        <button
          onClick={fetchStudents}
          className="p-2 bg-indigo-100 text-indigo-600 rounded-lg hover:bg-indigo-200 transition-colors"
        >
          <RefreshCw size={20} />
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-4">
        <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl p-4 text-white">
          <Users size={24} className="opacity-80 mb-2" />
          <p className="text-3xl font-bold">{stats.total}</p>
          <p className="text-indigo-200 text-sm">Total Students</p>
        </div>
        {Object.entries(stats.by_year).slice(0, 4).map(([year, count]) => (
          <div key={year} className="bg-white rounded-xl p-4 border-2 border-gray-200">
            <GraduationCap size={20} className="text-indigo-500 mb-2" />
            <p className="text-2xl font-bold text-gray-900">{count}</p>
            <p className="text-gray-500 text-sm">{year} Year</p>
          </div>
        ))}
      </div>

      {/* Top Colleges */}
      {Object.keys(stats.by_college).length > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-200">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <Building size={18} className="text-indigo-600" />
            Top Colleges
          </h3>
          <div className="flex flex-wrap gap-2">
            {Object.entries(stats.by_college).slice(0, 8).map(([college, count]) => (
              <span key={college} className="px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full text-sm">
                {college}: {count}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-6">
        {/* Students List */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <h3 className="font-semibold text-gray-800 mb-3">Registered Students</h3>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  placeholder="Search by name, email, college..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm"
                />
              </div>
              <select
                value={filterYear}
                onChange={(e) => setFilterYear(e.target.value)}
                className="px-3 py-2 border rounded-lg text-sm"
              >
                <option value="">All Years</option>
                <option value="1st">1st Year</option>
                <option value="2nd">2nd Year</option>
                <option value="3rd">3rd Year</option>
                <option value="4th">4th Year</option>
                <option value="PG">PG</option>
                <option value="PhD">PhD</option>
              </select>
            </div>
          </div>
          <div className="max-h-[500px] overflow-y-auto">
            {filteredStudents.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No students found</p>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Rank</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Student</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">College</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Score</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Solved</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStudents.map((student, idx) => (
                    <tr
                      key={student.id}
                      onClick={() => fetchStudentDetail(student.id)}
                      className={`border-b cursor-pointer hover:bg-indigo-50 transition-colors ${
                        selectedStudent?.id === student.id ? 'bg-indigo-50' : ''
                      }`}
                    >
                      <td className="px-4 py-3">
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                          idx < 3 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {idx + 1}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-gray-900 text-sm">{student.full_name}</p>
                        <p className="text-xs text-gray-500">{student.email}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-700">{student.college}</p>
                        <p className="text-xs text-gray-500">{student.department} • {student.year_of_study}</p>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="font-bold text-indigo-600">{student.total_score || 0}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                          {student.problems_solved || 0}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Student Detail */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <h3 className="font-semibold text-gray-800">
              {selectedStudent ? 'Student Profile' : 'Select a Student'}
            </h3>
          </div>
          
          {!selectedStudent ? (
            <p className="text-center text-gray-500 py-12">Select a student to view details</p>
          ) : (
            <div className="p-4">
              {/* Profile Header */}
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white text-2xl font-bold">
                  {selectedStudent.full_name?.charAt(0)}
                </div>
                <div className="flex-1">
                  <h4 className="text-xl font-bold text-gray-900">{selectedStudent.full_name}</h4>
                  <p className="text-gray-600">{selectedStudent.email}</p>
                  <p className="text-sm text-gray-500">{selectedStudent.phone}</p>
                </div>
              </div>

              {/* Academic Details */}
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <h5 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <GraduationCap size={16} className="text-indigo-600" />
                  Academic Details
                </h5>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-500">College</p>
                    <p className="font-semibold text-gray-800">{selectedStudent.college}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">University</p>
                    <p className="font-semibold text-gray-800">{selectedStudent.university}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Department</p>
                    <p className="font-semibold text-gray-800">{selectedStudent.department}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Year of Study</p>
                    <p className="font-semibold text-gray-800">{selectedStudent.year_of_study}</p>
                  </div>
                </div>
              </div>

              {/* Performance Stats */}
              <div className="bg-indigo-50 rounded-xl p-4 mb-4">
                <h5 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <BarChart3 size={16} className="text-indigo-600" />
                  Performance Stats
                </h5>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-white rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-indigo-600">{selectedStudent.total_score || 0}</p>
                    <p className="text-xs text-gray-500">Total Score</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-green-600">{selectedStudent.problems_solved || 0}</p>
                    <p className="text-xs text-gray-500">Problems Solved</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-amber-600">{selectedStudent.rank || '-'}</p>
                    <p className="text-xs text-gray-500">Rank</p>
                  </div>
                </div>
              </div>

              {/* Recent Submissions */}
              {studentSubmissions.length > 0 && (
                <div>
                  <h5 className="font-semibold text-gray-800 mb-3">Recent Submissions</h5>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {studentSubmissions.map((sub, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-sm">
                        <span className="font-medium text-gray-700">{sub.problem_id}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          sub.verdict === 'Accepted' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {sub.verdict}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Badges */}
              {selectedStudent.badges?.length > 0 && (
                <div className="mt-4">
                  <h5 className="font-semibold text-gray-800 mb-2">Badges</h5>
                  <div className="flex flex-wrap gap-2">
                    {selectedStudent.badges.map((badge, idx) => (
                      <span key={idx} className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm flex items-center gap-1">
                        <Award size={14} /> {badge}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CodeArenaStudentsAdmin;
